import { useRef, useState } from 'react';

interface UseTooltipFollowOptions {
  lag?: number; // 0 to 1, where 1 is instant and lower values add more lag
  offset?: { x: number; y: number };
}

export const useTooltipFollow = ({ lag = 0.05, offset = { x: 10, y: 10 } }: UseTooltipFollowOptions = {}) => {
  const ref = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const currentPos = useRef({ x: 0, y: 0 });
  const targetPos = useRef({ x: 0, y: 0 });
  const animationFrame = useRef<number | null>(null);

  const animate = () => {
    if (!tooltipRef.current) return;

    currentPos.current.x += (targetPos.current.x - currentPos.current.x) * lag;
    currentPos.current.y += (targetPos.current.y - currentPos.current.y) * lag;

    const rect = tooltipRef.current.getBoundingClientRect();
    const topOffset = -rect.height;

    tooltipRef.current.style.transform = `translate(${currentPos.current.x + offset.x}px, ${currentPos.current.y + offset.y + topOffset}px)`;

    const deltaX = Math.abs(targetPos.current.x - currentPos.current.x);
    const deltaY = Math.abs(targetPos.current.y - currentPos.current.y);

    if (deltaX > 0.5 || deltaY > 0.5) {
      animationFrame.current = requestAnimationFrame(animate);
    }
  };

  const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!ref.current) return;

    const rect = ref.current.getBoundingClientRect();

    targetPos.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };

    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
    }

    animationFrame.current = requestAnimationFrame(animate);
  };

  const onMouseEnter = () => {
    setIsHovered(true);
  };

  const onMouseLeave = () => {
    setIsHovered(false);

    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
    }
  };

  return {
    ref,
    tooltipRef,
    isHovered,
    onMouseMove,
    onMouseEnter,
    onMouseLeave,
  };
};
